import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Zap, Bell } from "lucide-react";
import { format } from "date-fns";

export default function CheckoutStep({ data, update, items = [], deliveryDate, setDeliveryDate }) {
  const [calOpen, setCalOpen] = useState(false);
  const [followUpCalOpen, setFollowUpCalOpen] = useState(false);
  const deliveryDateObj = deliveryDate ? new Date(deliveryDate) : undefined;
  const followUpDateObj = data.follow_up_date ? new Date(data.follow_up_date) : undefined;

  const grandTotal = items.reduce((s, i) => s + i.quantity * i.price_per_piece, 0);
  const balanceDue = grandTotal - (data.advance_paid || 0);

  return (
    <div className="animate-fade-in" data-testid="checkout-step">
      <h3 className="font-['Space_Mono'] text-lg tracking-tight uppercase text-[#5C4D24] mb-1">Financial & Checkout</h3>
      <p className="text-sm text-[#8B7536] mb-6">Review items and set payment details</p>

      {/* Items Summary */}
      <div className="border border-[#D6D6CF] mb-6">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-[#F5F5F0] border-b border-[#D6D6CF]">
          <span className="col-span-4 font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536]">Item</span>
          <span className="col-span-2 font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536]">Fabric</span>
          <span className="col-span-2 font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] text-right">Qty</span>
          <span className="col-span-2 font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] text-right">Price</span>
          <span className="col-span-2 font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] text-right">Subtotal</span>
        </div>
        {items.map((item, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-[#D6D6CF] last:border-0 text-sm" data-testid={`checkout-item-${idx}`}>
            <span className="col-span-4 text-[#5C4D24]">{item.garment_type || "Item"} {item.collection_name ? `- ${item.collection_name}` : ""}</span>
            <span className="col-span-2 text-[#8B7536] text-xs">{item.fabric_name || "N/A"}</span>
            <span className="col-span-2 font-['Space_Mono'] text-[#5C4D24] text-right">{item.quantity}</span>
            <span className="col-span-2 font-['Space_Mono'] text-[#5C4D24] text-right">{item.price_per_piece}</span>
            <span className="col-span-2 font-['Space_Mono'] text-[#5C4D24] text-right font-bold">{(item.quantity * item.price_per_piece).toFixed(0)}</span>
          </div>
        ))}
        <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-[#F5F5F0]">
          <span className="col-span-10 font-['Space_Mono'] text-xs tracking-widest uppercase text-[#9F7D23] text-right">Grand Total</span>
          <span className="col-span-2 font-['Space_Mono'] text-lg text-[#5C4D24] text-right font-bold" data-testid="grand-total">{grandTotal.toFixed(0)} AED</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6 max-w-3xl">
        <div>
          <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-2">Advance Paid (AED)</label>
          <input type="number" value={data.advance_paid || ""} onChange={(e) => { const v = parseFloat(e.target.value) || 0; update({ advance_paid: v }); }} placeholder="0" data-testid="advance-paid-input" className="tinkah-input text-xl" min="0" />
        </div>
        <div>
          <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-2">Balance Due (AED)</label>
          <div className="tinkah-input text-xl font-bold text-[#9F3A3A]" data-testid="balance-due-display">{balanceDue.toFixed(2)}</div>
        </div>
        <div>
          <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-2">Payment Method</label>
          <Select value={data.payment_method} onValueChange={(v) => update({ payment_method: v })}>
            <SelectTrigger className="rounded-none font-['Space_Mono'] text-sm border-0 border-b-2 border-[#D6D6CF] focus:border-[#9F7D23] px-0" data-testid="payment-method-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="card">Card</SelectItem>
              <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-2">Expected Delivery Date</label>
          <Popover open={calOpen} onOpenChange={setCalOpen}>
            <PopoverTrigger asChild>
              <button data-testid="delivery-date-btn" className="tinkah-input text-left flex items-center gap-2 cursor-pointer">
                <CalendarIcon className="w-4 h-4 text-[#9F7D23]" />
                <span className={deliveryDate ? "text-[#5C4D24]" : "text-[rgba(139,117,54,0.4)]"}>{deliveryDate ? format(new Date(deliveryDate), "PPP") : "Select date"}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 rounded-none border-[#D6D6CF]" align="start">
              <Calendar mode="single" selected={deliveryDateObj} onSelect={(d) => { setDeliveryDate(d ? d.toISOString() : ""); setCalOpen(false); }} disabled={(d) => d < new Date()} data-testid="delivery-calendar" />
            </PopoverContent>
          </Popover>
        </div>
        
        {/* Follow-up Date for Balance Payment */}
        {balanceDue > 0 && (
          <div>
            <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-2 flex items-center gap-2">
              <Bell className="w-3 h-3" /> Payment Follow-up Date
            </label>
            <Popover open={followUpCalOpen} onOpenChange={setFollowUpCalOpen}>
              <PopoverTrigger asChild>
                <button data-testid="followup-date-btn" className="tinkah-input text-left flex items-center gap-2 cursor-pointer">
                  <CalendarIcon className="w-4 h-4 text-[#D6AD60]" />
                  <span className={data.follow_up_date ? "text-[#5C4D24]" : "text-[rgba(139,117,54,0.4)]"}>
                    {data.follow_up_date ? format(new Date(data.follow_up_date), "PPP") : "Set reminder date"}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 rounded-none border-[#D6D6CF]" align="start">
                <Calendar 
                  mode="single" 
                  selected={followUpDateObj} 
                  onSelect={(d) => { 
                    update({ follow_up_date: d ? d.toISOString().split('T')[0] : "" }); 
                    setFollowUpCalOpen(false); 
                  }} 
                  disabled={(d) => d < new Date()} 
                  data-testid="followup-calendar" 
                />
              </PopoverContent>
            </Popover>
          </div>
        )}
        
        <div className="md:col-span-2">
          <div className="flex items-center gap-4 pt-4 border-t border-[#D6D6CF]">
            <Switch checked={data.is_urgent} onCheckedChange={(v) => update({ is_urgent: v })} data-testid="urgent-toggle" className="data-[state=checked]:bg-[#9F3A3A]" />
            <div className="flex items-center gap-2">
              <Zap className={`w-4 h-4 ${data.is_urgent ? "text-[#9F3A3A]" : "text-[#D6D6CF]"}`} />
              <span className="font-['Space_Mono'] text-xs tracking-widest uppercase text-[#5C4D24]">Urgent Order</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
